import { getDoctorFullName } from '@lmt/unicorn-shared/app/components/doctors/doctor-form/common/common';
import { useGlobalForms } from '@lmt/unicorn-shared/app/components/global-forms/useGlobalForms';
import {
  FullHeightGridRoot,
  GetStyledRow,
  HoverableTable,
  StickyHeaderRow,
} from '@lmt/unicorn-shared/app/helpers/devextreme-helper';
import React, { FC, useCallback, useMemo } from 'react';
import { Paper } from '@mui/material';
import { DoctorListDto, DoctorType } from '@lmt/shared-types';
import {
  CustomDictionaryName,
  useActiveDictionaryValuesByTitle,
} from '@lmt/unicorn-shared/app/api/custom-hooks';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';

import {
  DXRG,
  IntegratedSorting,
  Sorting,
  useReactDataGrid,
} from '@lmt/unicorn-shared/app/common/devexpress/ReactGrid/dx-react-grid';
import { useReactDataGridLocalization } from '@lmt/unicorn-shared/app/common/devexpress/ReactGrid/dx-react-grid-localization';

const DoctorTableKeys: { [dictionary: string]: string } = {
  id: 'id',
  name: 'name',
  specialty: 'specialty',
  active: 'active',
  subtenants: 'subtenants',
};

type Props = {
  isNoDoctorsFound: boolean;
  data: DoctorListDto[];
  skip: number;
  totalRowCount: number;
  pageSize?: number;
  getRows: (skip: number, take: number) => void;
  isLoading: boolean;
  sorting: Sorting[];
  setSorting: (value: Sorting[]) => void;
  doctorType: DoctorType;
};

export const DoctorsTable: FC<Props> = React.memo(function DoctorsTable(props) {
  useReactDataGrid();
  const localizationMessages = useReactDataGridLocalization();

  const i18next = useScopedTranslation('doctors.list');
  const forms = useGlobalForms();
  const openDoctorEditingForm = useCallback(
    (doctorId: number) =>
      forms.openDoctorEditing(props.doctorType, doctorId, true),
    [history],
  );
  const { data: specialties = [] } = useActiveDictionaryValuesByTitle(
    CustomDictionaryName.Specialties,
  );

  const sortingColumnExtensions = useMemo<IntegratedSorting.ColumnExtension[]>(
    () => [
      {
        columnName: DoctorTableKeys.name,
        compare: (a: string, b: string) => a.localeCompare(b),
      },
    ],
    [],
  );

  const columns = useMemo(
    () => [
      {
        name: DoctorTableKeys.name,
        title: i18next.t(`columns.${DoctorTableKeys.name}` as any),
        getCellValue: getDoctorFullName,
      },
      {
        name: DoctorTableKeys.specialty,
        title: i18next.t(`columns.${DoctorTableKeys.specialty}` as any),
        getCellValue: (row: DoctorListDto) =>
          specialties.find((x) => x.id === row.specialtyId)?.value ?? '',
      },
      {
        name: DoctorTableKeys.active,
        title: i18next.t(`columns.${DoctorTableKeys.active}` as any),
        getCellValue: (row: DoctorListDto) =>
          i18next.t(`statuses.${row.active ? 'active' : 'not_active'}`),
      },
    ],
    [specialties],
  );

  const notSortableColumns = useMemo(() => {
    return Object.values(DoctorTableKeys)
      .filter((x) => !props.sorting.map((s) => s.columnName).includes(x))
      .map((x) => {
        return {
          columnName: x,
          sortingEnabled: false,
        };
      });
  }, props.sorting);

  return (
    <Paper style={{ height: '100%', width: '100%' }}>
      <DXRG.Grid
        rows={props.data ?? []}
        columns={columns}
        getRowId={(doc) => doc.id}
        rootComponent={FullHeightGridRoot}
      >
        <DXRG.SortingState
          sorting={props.sorting}
          onSortingChange={props.setSorting}
          columnExtensions={notSortableColumns}
        />
        <DXRG.IntegratedSorting columnExtensions={sortingColumnExtensions} />
        <DXRG.VirtualTableState
          infiniteScrolling={false}
          skip={props.skip}
          totalRowCount={props.totalRowCount}
          loading={props.isLoading}
          getRows={props.getRows}
          pageSize={props.pageSize}
        />
        <DXRG.VirtualTable
          height={'auto'}
          messages={localizationMessages}
          tableComponent={HoverableTable}
          rowComponent={GetStyledRow((row) => openDoctorEditingForm(row.id))}
        />
        <DXRG.TableHeaderRow
          showSortingControls
          rowComponent={StickyHeaderRow}
        />
      </DXRG.Grid>
    </Paper>
  );
});
